import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
  ActivityIndicator,
  Modal,
  TouchableOpacity,
} from 'react-native';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import CheckerPattern from '../components/CheckerPattern';
import RegistrationFormContent from '../components/RegistrationFormContent';
import { submitHotspotClaimRequest } from '../services/database';
import { sendAdminNotificationEmail } from '../services/emailService';
import { supabase, STORAGE_BUCKETS, getPublicUrl } from '../services/supabase';
import { uploadImageToStorage as uploadImageToStorageUtil } from '../utils/uploadImageToStorage';

const CompleteProfileScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(false);
  const [oauthError, setOauthError] = useState('');
  // Initialize from sessionStorage to survive page reload after redirect
  const [oauthProfileDone, setOauthProfileDone] = useState(() => {
    if (Platform.OS === 'web') {
      try {
        const done = sessionStorage.getItem('chessmaps_profile_done') === '1';
        if (done) sessionStorage.removeItem('chessmaps_profile_done');
        return done;
      } catch (_) {}
    }
    return false;
  });
  // Ref to synchronously track when OAuth flow started — prevents old wizard flash
  const oauthFlowActiveRef = useRef(false);
  // When Clerk had no session at Complete, we poll for session instead of redirecting to Google again
  const [waitingForClerk, setWaitingForClerk] = useState(null); // { profileData, formData, isClub, email }
  
  const { updateUserProfile, createUserProfile, syncUserWithDatabase, user, appUser, authState, completeSignUpWithUsername, signOut, loading: authLoading, isSignedIn } = useAuth();
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();

  // Theme colors
  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
    card: isDarkMode ? '#141414' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#0A0A0A',
    textSecondary: isDarkMode ? '#888888' : '#666666',
    border: isDarkMode ? '#2A2A2A' : '#E5E5E5',
    inputBg: isDarkMode ? '#1A1A1A' : '#F5F5F5',
    accent: '#94EB98',
    accentDark: '#7BC97F',
    error: '#FF6B6B',
  };

  // Lock body scroll on web when modal is open
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    if (authState?.needsCompleteProfile && !oauthProfileDone) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [authState?.needsCompleteProfile, oauthProfileDone]);

  // If we're showing "Setting up your account..." but user is not actually signed in
  // (e.g. profile was created with placeholder clerk_id and they were redirected),
  // unstick after a short delay so they can sign in again.
  useEffect(() => {
    if (!oauthProfileDone || isSignedIn) return;
    const t = setTimeout(() => {
      try { sessionStorage.removeItem('chessmaps_profile_done'); } catch (_) {}
      setOauthProfileDone(false);
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.replace('/');
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [oauthProfileDone, isSignedIn]);

  // When Clerk had no session at Complete we stay on this page and poll until Clerk has a session,
  // then create the user and finish (no redirect to Google "choose an account").
  useEffect(() => {
    if (!waitingForClerk) return;
    let cancelled = false;
    const { profileData, formData, isClub, email } = waitingForClerk;
    const maxAttempts = 20; // ~30s total so Clerk has time to establish session on same page
    const intervalMs = 1500;
    const poll = async () => {
      let profileDataToTry = { ...profileData };
      let triedUsernameFallback = false;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) return;
        const result = await createUserProfile(profileDataToTry);
        if (cancelled) return;
        if (result.success && !result.usedPlaceholderClerkId && result.user) {
          const newUserId = result.user.id;
          const effectiveUsername = result.user.username ?? formData.username;
          if (isClub) {
            let evidenceUrls = [];
            if (formData.evidenceImages?.length > 0 && newUserId) {
              for (let i = 0; i < formData.evidenceImages.length; i++) {
                const res = await uploadImageToStorage(
                  STORAGE_BUCKETS.CLUB_EVIDENCE,
                  formData.evidenceImages[i],
                  `${newUserId}/evidence-${i}`
                );
                const url = res?.url ?? res;
                if (url) evidenceUrls.push(url);
              }
            }
            if (formData.clubMode === 'CLAIM' && formData.selectedClub) {
              try {
                await submitHotspotClaimRequest({
                  hotspotId: formData.selectedClub.id,
                  requesterUserId: newUserId,
                  email,
                  name: effectiveUsername,
                  proofDescription: formData.claimProofDescription || '',
                  contactInfo: formData.claimContactInfo || null,
                  evidenceUrls: evidenceUrls.length > 0 ? evidenceUrls : null,
                });
              } catch (e) {
                console.error('Claim request submission error:', e);
              }
            }
            const clubDisplayName = formData.clubName || formData.selectedClub?.name || effectiveUsername;
            const pitchText = formData.clubMode === 'CLAIM'
              ? `CLAIM REQUEST: ${formData.claimProofDescription || ''}`
              : formData.submissionPitch || '';
            const emailResult = await sendAdminNotificationEmail({
              clubName: clubDisplayName,
              email,
              pitch: pitchText,
              address: formData.clubAddress || formData.selectedClub?.address || 'N/A',
              socialLinks: {
                website: formData.websiteUrl || null,
                instagram: formData.instagramUrl || null,
                twitter: formData.twitterUrl || null,
                facebook: formData.facebookUrl || null,
                tiktok: formData.tiktokUrl || null,
              },
              isClaimRequest: formData.clubMode === 'CLAIM',
              evidenceUrls: evidenceUrls || [],
              contactInfo: formData.claimContactInfo || null,
            });
            if (!emailResult.success) {
              console.warn('⚠️ Admin notification email failed:', emailResult.error);
              showToast('Account created! Admin email notification failed.', 'warning');
            }
          }
          if (formData.profileImage && (formData.profileImage.uri || formData.profileImage.base64 || formData.profileImage.file || formData.profileImage.arrayBuffer) && newUserId) {
            const picResult = await uploadImageToStorage(
              STORAGE_BUCKETS.PROFILE_PICTURES,
              formData.profileImage,
              `${newUserId}/profile`
            );
            const picPath = picResult?.path ?? picResult;
            if (picPath) {
              try {
                const picUp = await updateUserProfile({ profilePictureUrl: picPath }, { userId: newUserId });
                if (!picUp.success) throw new Error(picUp.error || 'Profile picture update failed');
                if (syncUserWithDatabase) await syncUserWithDatabase();
                await new Promise((r) => setTimeout(r, 400));
                if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
                  try {
                    sessionStorage.setItem('chessmaps_pending_profile_pic', JSON.stringify({ path: picPath, userId: newUserId }));
                  } catch (_) {}
                }
              } catch (e) {
                console.error('Failed to save profile picture URL:', e);
                showToast('Profile photo saved but update failed — you can change it in Profile.', 'error');
              }
            }
          }
          setWaitingForClerk(null);
          setOauthProfileDone(true);
          showToast(
            isClub ? 'Account created! Your club application is under review.' : 'Profile setup complete! Welcome to ChessMaps',
            'success'
          );
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            try {
              sessionStorage.setItem('chessmaps_profile_done', '1');
              sessionStorage.setItem('chessmaps_welcome_toast', '1');
            } catch (_) {}
            window.location.replace('/');
          }
          return;
        }
        if (
          !result.success &&
          !triedUsernameFallback &&
          /username|already exists|taken/i.test(result?.error || '')
        ) {
          try {
            const base = (profileDataToTry.username || formData.username || 'user')
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9_]/g, '')
              .slice(0, 18);
            const fallbackUsername = `${base}_${Math.random().toString(36).slice(2, 7)}`;
            profileDataToTry = { ...profileDataToTry, username: fallbackUsername };
            triedUsernameFallback = true;
            continue;
          } catch (_) {
            // If fallback generation fails, just keep retrying until maxAttempts.
          }
        }
        if (attempt < maxAttempts) await new Promise(r => setTimeout(r, intervalMs));
      }
      if (cancelled) return;
      setWaitingForClerk(null);
      // Don't set chessmaps_reauth_google — don't auto-send user to Google "choose account".
      // Store profile and a message for the login page (toast is lost on redirect).
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        try {
          sessionStorage.setItem('chessmaps_pending_profile', JSON.stringify(profileData));
          sessionStorage.setItem('chessmaps_pending_toast', 'Account created. Sign in with Google to continue.');
        } catch (_) {}
        window.location.replace('/login');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [waitingForClerk]);

  const uploadImageToStorage = (bucket, imageAsset, pathPrefix) =>
    uploadImageToStorageUtil(supabase, bucket, imageAsset, pathPrefix, (msg) => showToast(`Photo upload failed: ${msg}`, 'error'), getPublicUrl);

  // OAuth new user: show full registration form (account type, club/player, claim/create)
  const handleOAuthCompleteProfile = async (data) => {
    if (data?.error) {
      setOauthError(data.error);
      return;
    }
    setOauthError('');
    setLoading(true);
    try {
      let createdUserId = null;
      const email = user?.emailAddresses?.[0]?.emailAddress || authState?.oauthPendingEmail || data.email || '';
      if (authState?.needsUsername && completeSignUpWithUsername) {
        const signUpResult = await completeSignUpWithUsername(data.username);
        if (!signUpResult.success) {
          const msg = signUpResult.error || '';
          const isUsernameOnlyError = /username|already exists|taken/i.test(msg);

          if (isUsernameOnlyError) {
            console.warn('⚠️ Ignoring Clerk username conflict during OAuth completion:', msg);
          } else {
            setOauthError(signUpResult.error || 'Failed to complete sign-up.');
            setLoading(false);
            return;
          }
        }
        // Always try to capture createdUserId (the fix above should return it even for username-taken)
        if (signUpResult.createdUserId) {
          createdUserId = signUpResult.createdUserId;
          await new Promise(r => setTimeout(r, 1200));
        }
      }
      // Always pass email so profile creation can proceed even if Clerk user
      // context is still catching up; include clerkUserId when we have it.
      const profileOptions = {
        clerkEmail: email,
        ...(createdUserId ? { clerkUserId: createdUserId } : {}),
      };

      // Retry helper for session propagation timing. Automatically retries
      // "session not ready" style errors so the user doesn't have to tap twice.
      const tryCreateProfile = async (profileData, opts, maxAttempts = 8) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const result = await createUserProfile(profileData, opts);

          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/b52ea7f9-772d-4699-bf57-9767c6a601fb', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Debug-Session-Id': '81b6b1',
            },
            body: JSON.stringify({
              sessionId: '81b6b1',
              runId: 'pre-fix-1',
              hypothesisId: 'H2',
              location: 'CompleteProfileScreen.js:tryCreateProfile',
              message: 'Result from createUserProfile attempt',
              data: {
                attempt,
                success: !!result?.success,
                error: result?.error || null,
              },
              timestamp: Date.now(),
            }),
          }).catch(() => {});
          // #endregion agent log

          if (result.success) return result;
          const msg = result.error || '';
          const isSessionError = /session|signed in|credentials|not ready/i.test(msg);
          if (!isSessionError || attempt === maxAttempts) return result;
          // Small backoff so Clerk has time to finalize the session
          const delay = 400 + (attempt - 1) * 200; // 400ms, 600ms, 800ms, ...
          await new Promise(r => setTimeout(r, delay));
        }
        return { success: false, error: 'Failed after retries' };
      };

      const isClub = data.accountType === 'CLUB';

      // ────────────────────────────────────────────────
      // STEP 1: Create the user profile (basic — no profile pic yet)
      // ────────────────────────────────────────────────
      const commonProfileFields = {
        username: data.username,
        email,
        authProvider: 'oauth',
        rating: 1200,
        gamesPlayedInApp: 0,
        ...(data.city ? { city: data.city } : {}),
        ...(data.bio ? { bio: data.bio } : {}),
      };

      let profileData;
      if (!isClub) {
        profileData = {
          ...commonProfileFields,
          accountType: 'PLAYER',
          chesscomUsername: data.chesscomUsername || null,
          lichessUsername: data.lichessUsername || null,
          websiteUrl: data.websiteUrl || null,
          instagramUrl: data.instagramUrl || null,
          twitterUrl: data.twitterUrl || null,
          facebookUrl: data.facebookUrl || null,
          tiktokUrl: data.tiktokUrl || null,
        };
      } else {
        profileData = {
          ...commonProfileFields,
          accountType: 'CLUB',
          clubName: data.clubName || data.selectedClub?.name || data.username,
          address: data.clubAddress || data.selectedClub?.address || '',
          submissionPitch: data.submissionPitch || data.claimProofDescription || '',
          website: data.websiteUrl || null,
          instagramUrl: data.instagramUrl || null,
          twitterUrl: data.twitterUrl || null,
          facebookUrl: data.facebookUrl || null,
          tiktokUrl: data.tiktokUrl || null,
          approvalStatus: 'PENDING',
          submittedAt: new Date().toISOString(),
          lastSubmissionAttempt: new Date().toISOString(),
        };
      }

      let createResult = await tryCreateProfile(profileData, profileOptions);
      // If Supabase rejects because the username is already taken, retry once with a
      // fallback unique username so OAuth completion can finish.
      if (!createResult.success) {
        const msg = createResult.error || '';
        const isUsernameTaken = /username|already exists|taken/i.test(msg);
        if (isUsernameTaken) {
          try {
            const base = (data.username || 'user')
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9_]/g, '')
              .slice(0, 18);
            const fallbackUsername = `${base}_${Math.random().toString(36).slice(2, 7)}`;
            const retryProfileData = { ...profileData, username: fallbackUsername };
            createResult = await tryCreateProfile(retryProfileData, profileOptions);
          } catch (e) {
            // If retry logic itself fails, fall through to the original error below.
          }
        }
      }

      if (!createResult.success) {
        setOauthError(createResult.error || 'Failed to create profile.');
        setLoading(false);
        return;
      }

      const effectiveUsername = createResult.user?.username ?? data.username;

      // Prefer created user id; fallback to appUser.id (AuthContext sets appUser after create)
      const newUserId = createResult.user?.id ?? appUser?.id;

      // ────────────────────────────────────────────────
      // STEP 2 (CLUB ONLY): Upload evidence + send admin email — must succeed before profile setup
      // ────────────────────────────────────────────────
      if (isClub) {
        // 2a. Upload evidence images
        let evidenceUrls = [];
        if (data.evidenceImages?.length > 0 && newUserId) {
          for (let i = 0; i < data.evidenceImages.length; i++) {
            const res = await uploadImageToStorage(
              STORAGE_BUCKETS.CLUB_EVIDENCE,
              data.evidenceImages[i],
              `${newUserId}/evidence-${i}`
            );
            const url = res?.url ?? res;
            if (url) evidenceUrls.push(url);
          }
        }

        // 2b. If claiming, submit the claim request
        if (data.clubMode === 'CLAIM' && data.selectedClub) {
          try {
            await submitHotspotClaimRequest({
              hotspotId: data.selectedClub.id,
              requesterUserId: newUserId || null,
              email,
              name: effectiveUsername,
              proofDescription: data.claimProofDescription || '',
              contactInfo: data.claimContactInfo || null,
              evidenceUrls: evidenceUrls.length > 0 ? evidenceUrls : null,
            });
          } catch (e) {
            console.error('Claim request submission error:', e);
          }
        }

        // 2c. Send admin notification email with evidence links
        const clubDisplayName = data.clubName || data.selectedClub?.name || effectiveUsername;
        const pitchText = data.clubMode === 'CLAIM'
          ? `CLAIM REQUEST: ${data.claimProofDescription || ''}`
          : data.submissionPitch || '';
        const emailResult = await sendAdminNotificationEmail({
          clubName: clubDisplayName,
          email,
          pitch: pitchText,
          address: data.clubAddress || data.selectedClub?.address || 'N/A',
          socialLinks: {
            website: data.websiteUrl || null,
            instagram: data.instagramUrl || null,
            twitter: data.twitterUrl || null,
            facebook: data.facebookUrl || null,
            tiktok: data.tiktokUrl || null,
          },
          isClaimRequest: data.clubMode === 'CLAIM',
          evidenceUrls,
          contactInfo: data.claimContactInfo || null,
        });

        if (!emailResult.success) {
          // Email is a notification only — don't block account creation.
          // The admin can still see the request in the admin tools panel.
          console.warn('⚠️ Admin notification email failed:', emailResult.error);
          showToast('Account created! Admin email notification failed — your request is still visible in the admin panel.', 'warning');
        }
      }

      // ────────────────────────────────────────────────
      // STEP 3: Profile setup — upload profile picture + update record
      // ────────────────────────────────────────────────
      console.log('📸 Profile image data:', data.profileImage ? {
        hasUri: !!data.profileImage.uri,
        hasBase64: !!data.profileImage.base64,
        hasFile: !!data.profileImage.file,
        hasArrayBuffer: !!data.profileImage.arrayBuffer,
      } : 'NONE');
      if (data.profileImage && (data.profileImage.uri || data.profileImage.base64 || data.profileImage.file || data.profileImage.arrayBuffer) && newUserId) {
        const picResult = await uploadImageToStorage(
          STORAGE_BUCKETS.PROFILE_PICTURES,
          data.profileImage,
          `${newUserId}/profile`
        );
        const picPath = picResult?.path ?? picResult;
        if (picPath) {
          try {
            const picUp = await updateUserProfile({ profilePictureUrl: picPath }, { userId: newUserId });
            if (!picUp.success) throw new Error(picUp.error || 'Profile picture update failed');
            // Refetch app user from DB so profile picture is in auth state before redirect
            if (syncUserWithDatabase) await syncUserWithDatabase();
            await new Promise((r) => setTimeout(r, 400));
            // Safety net: persist so we can re-apply on next load if DB didn't get it
            if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
              try {
                sessionStorage.setItem('chessmaps_pending_profile_pic', JSON.stringify({ path: picPath, userId: newUserId }));
              } catch (_) {}
            }
          } catch (e) {
            console.error('Failed to save profile picture URL:', e);
            showToast('Profile photo saved but update failed — you can change it in Profile.', 'error');
          }
        }
      }

      // ────────────────────────────────────────────────
      // DONE — redirect (or ask user to sign in again if we used a placeholder clerk_id)
      // ────────────────────────────────────────────────
      const usedPlaceholderClerkId = createResult.usedPlaceholderClerkId === true;
      if (usedPlaceholderClerkId && createResult.pendingProfileData) {
        // Clerk had no session — stay on this page and poll until Clerk has a session, then create user.
        // Avoids redirecting to /login and triggering "choose an account" again.
        showToast('Setting up your account…', 'success');
        setLoading(false);
        setWaitingForClerk({
          profileData: createResult.pendingProfileData,
          formData: data,
          isClub,
          email,
        });
        return;
      }
      setOauthProfileDone(true);
      showToast(
        isClub
          ? 'Account created! Your club application is under review.'
          : 'Profile setup complete! Welcome to ChessMaps',
        'success'
      );
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        try {
          sessionStorage.setItem('chessmaps_profile_done', '1');
          sessionStorage.setItem('chessmaps_welcome_toast', '1');
        } catch (_) {}
        window.location.replace('/');
      }
      return;
    } catch (err) {
      setOauthError(err?.message || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const suggestedUsernameFromEmail = (authState?.oauthPendingEmail || user?.emailAddresses?.[0]?.emailAddress || '')
    .split('@')[0]?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';

  // Profile was just created (or we're waiting for Clerk session) — show loading until redirect or done
  if (oauthProfileDone || waitingForClerk) {
    return (
      <View style={[styles.container, { backgroundColor: theme.bg, justifyContent: 'center', alignItems: 'center' }]}>
        <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
        <ActivityIndicator size="large" color={theme.accent} />
        <Text style={{ color: theme.text, marginTop: 16, fontSize: 16 }}>
          {waitingForClerk ? 'Setting up your account…' : 'Setting up your account...'}
        </Text>
      </View>
    );
  }

  if (authState?.needsCompleteProfile || oauthFlowActiveRef.current) {
    // Mark the ref so the old wizard never renders even during state transitions
    oauthFlowActiveRef.current = true;
    return (
      <View style={[styles.container, { backgroundColor: theme.bg, overflow: 'hidden' }]}>
        <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
        <View style={styles.patternContainer}><CheckerPattern /></View>
        <Modal visible animationType="slide" transparent>
          <View style={[styles.oauthModalOverlay, Platform.OS === 'web' && styles.oauthModalOverlayWeb]}>
            <View style={[styles.oauthModalContent, { backgroundColor: theme.card }]}>
              <View style={styles.oauthModalHeader}>
                <Text style={[styles.oauthModalTitle, { color: theme.text }]}>Complete your profile</Text>
                <Text style={[styles.oauthModalSubtitle, { color: theme.textSecondary }]}>
                  You signed in with Google. Choose your account type to finish setup.
                </Text>
              </View>
              <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.oauthFormWrapper}>
                <RegistrationFormContent
                  isOAuth
                  suggestedUsername={suggestedUsernameFromEmail}
                  displayEmail={authState?.oauthPendingEmail || user?.emailAddresses?.[0]?.emailAddress || ''}
                  onSubmit={handleOAuthCompleteProfile}
                  loading={loading}
                  errorMessage={oauthError}
                  onClearError={() => setOauthError('')}
                  theme={theme}
                  showEmailPassword={false}
                  showSocialNote={false}
                  submitLabel="Complete Setup"
                  headerTagline="Choose Player or Club, then fill in the details below."
                />
                <TouchableOpacity
                  style={[styles.useDifferentAccountButton, { borderColor: theme.border }]}
                  onPress={() => signOut?.()}
                  disabled={!signOut}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.useDifferentAccountText, { color: theme.textSecondary }]}>
                    Use a different account
                  </Text>
                </TouchableOpacity>
              </KeyboardAvoidingView>
            </View>
          </View>
        </Modal>
      </View>
    );
  }

  // Fallback: if somehow we reached here without needsCompleteProfile, redirect to the map.
  // This replaces the old legacy 3-step wizard that is no longer needed.
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    // Redirect on next tick so the loading UI renders first
    setTimeout(() => window.location.replace('/'), 0);
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.bg, justifyContent: 'center', alignItems: 'center' }]}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
      <ActivityIndicator size="large" color={theme.accent} />
      <Text style={{ color: theme.text, marginTop: 16, fontSize: 16 }}>Loading...</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  oauthModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    overflow: 'hidden',
  },
  oauthModalOverlayWeb: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20000,
    overflow: 'hidden',
  },
  oauthModalContent: {
    borderRadius: 24,
    padding: 24,
    maxHeight: '90%',
    width: '100%',
    maxWidth: 500,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 10,
  },
  oauthModalHeader: {
    marginBottom: 20,
  },
  oauthModalTitle: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 8,
  },
  oauthModalSubtitle: {
    fontSize: 14,
    lineHeight: 20,
  },
  oauthFormWrapper: {
    flex: 1,
  },
  useDifferentAccountButton: {
    marginTop: 20,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignSelf: 'center',
    borderWidth: 1,
    borderRadius: 8,
  },
  useDifferentAccountText: {
    fontSize: 14,
  },
  patternContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.03,
  },
});

export default CompleteProfileScreen;
